import React, { useState, useEffect } from 'react';
import { testimonialsAPI } from '../../utils/api';
import Card from '../common/Card';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import { FaQuoteLeft, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const TestimonialsCarousel = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        const response = await testimonialsAPI.getFeatured();
        setTestimonials(response.data.data || []);
      } catch (err) {
        setError(err.message || 'Failed to load testimonials');
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  if (loading) {
    return (
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <Loading size="lg" text="Loading testimonials..." />
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ErrorMessage message={error} />
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return null;
  }

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-20 md:py-28 bg-primary-warm">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <h2 className="text-4xl md:text-5xl lg:text-display font-heading font-bold text-secondary mb-6 leading-tight">
            What Clients Say
          </h2>
          <p className="text-lg md:text-xl text-text-secondary font-body max-w-2xl mx-auto leading-relaxed">
            Hear from those who have experienced our photography
          </p>
        </div>

        {/* Testimonial card */}
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 md:p-12 lg:p-16 bg-gradient-to-br from-white to-bg-secondary shadow-soft-lg">
            <div className="text-center">
              <FaQuoteLeft className="text-accent-secondary text-5xl md:text-6xl mx-auto mb-8 opacity-40" />

              <blockquote className="text-xl md:text-2xl lg:text-3xl font-body text-text-primary mb-8 italic leading-relaxed">
                "{currentTestimonial.text}"
              </blockquote>

              <div className="flex items-center justify-center space-x-3">
                <p className="font-heading font-bold text-secondary text-lg md:text-xl">
                  {currentTestimonial.author}
                </p>
                <span className="text-text-light">•</span>
                <p className="text-text-secondary font-body capitalize">
                  {currentTestimonial.relationship}
                </p>
              </div>
            </div>

            {/* Navigation */}
            {testimonials.length > 1 && (
              <div className="flex justify-center items-center space-x-6 mt-10">
                <button
                  onClick={prevTestimonial}
                  className="p-3 rounded-full bg-accent-light hover:bg-accent-secondary text-accent-secondary hover:text-white transition-all duration-300"
                  aria-label="Previous testimonial"
                >
                  <FaChevronLeft size={18} />
                </button>

                <div className="flex space-x-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        index === currentIndex ? 'bg-accent w-10' : 'bg-gray-300 w-2'
                      }`}
                      aria-label={`Go to testimonial ${index + 1}`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextTestimonial}
                  className="p-3 rounded-full bg-accent-light hover:bg-accent-secondary text-accent-secondary hover:text-white transition-all duration-300"
                  aria-label="Next testimonial"
                >
                  <FaChevronRight size={18} />
                </button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
